import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { useState, useEffect } from 'react';

// Function to get country flag using two-letter code
const getCountryFlag = countryCode => {
  return countryCode
    .toUpperCase()
    .split('')
    .map(char => String.fromCodePoint(char.charCodeAt(0) + 127397))
    .join('');
};

// Get all countries and their data
const countries = getCountries()
  .map(country => ({
    code: `+${getCountryCallingCode(country)}`,
    country,
    flag: getCountryFlag(country),
    name: new Intl.DisplayNames(['en'], { type: 'region' }).of(country),
  }))
  .sort((a, b) => {
    // Put UK first, then sort alphabetically by name
    if (a.country === 'GB') return -1;
    if (b.country === 'GB') return 1;
    return a.name.localeCompare(b.name);
  });

function PhoneInput({
  value = '',
  onChange,
  onBlur,
  name,
  error,
  touched,
  label,
  required = false,
}) {
  const [countryCode, setCountryCode] = useState('+44');
  const [phoneNumber, setPhoneNumber] = useState(value);
  const selectedCountry = countries.find(c => c.code === countryCode);

  useEffect(() => {
    setPhoneNumber(value);
  }, [value]);

  const handleCountryCodeChange = e => {
    const newCode = e.target.value;
    setCountryCode(newCode);
  };

  const handlePhoneNumberChange = e => {
    const newNumber = e.target.value.replace(/[^\d\s]/g, '');
    setPhoneNumber(newNumber);
    onChange({
      target: {
        name,
        value: newNumber,
      },
    });
  };

  return (
    <div>
      {label && (
        <label className="block text-gray-300 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="flex gap-2">
        <div className="relative w-28">
          <select
            value={countryCode}
            onChange={handleCountryCodeChange}
            className="w-full p-2 rounded bg-hdlcc-darkGray text-transparent border border-hdlcc-gray appearance-none focus:border-hdlcc-lightBlue focus:outline-none
            [&:-webkit-autofill]:bg-hdlcc-darkGray [&:-webkit-autofill]:text-transparent
            [&:-webkit-autofill]:[-webkit-text-fill-color:transparent]
            [&:-webkit-autofill]:[transition-delay:9999s]"
            title="Select country code"
          >
            {countries.map(({ code, country, flag, name }) => (
              <option key={country} value={code} className="bg-hdlcc-darkGray text-white">
                {flag} {code} - {name}
              </option>
            ))}
          </select>
          {/* Custom display for selected value */}
          <div
            className="pointer-events-none absolute inset-y-0 left-0 right-8 flex items-center px-2 text-white"
            aria-hidden="true"
          >
            {selectedCountry?.flag} {selectedCountry?.code}
          </div>
          {/* Custom dropdown arrow */}
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
            <svg
              className="h-4 w-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          onBlur={onBlur}
          name={name}
          autoComplete="tel"
          className={`flex-1 p-2 rounded bg-hdlcc-darkGray text-white border ${
            touched && error ? 'border-red-500' : 'border-hdlcc-gray'
          } focus:border-hdlcc-lightBlue focus:outline-none placeholder-gray-400
          [&:-webkit-autofill]:!bg-hdlcc-darkGray
          [&:-webkit-autofill]:!text-white
          [&:-webkit-autofill]:![-webkit-text-fill-color:white]
          [&:-webkit-autofill]:![transition-delay:0s]
          [&:-webkit-autofill]:![-webkit-box-shadow:0_0_0_50px_rgb(26,26,26)_inset]`}
          placeholder="Phone number"
          required={required}
        />
      </div>
      {touched && error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}

export default PhoneInput;
